import type { Vec3 } from '../../geo/types';
import { transformPoint, type Frame } from './frameMath';
import type { MeshBuilder } from './meshBuilder';

/* Heights reach the floor along different sums, which differ in their last bits. */
const AT_FLOOR_M = 1e-4;
/* Two points of a face closer than this are one corner, and no strip hangs between them. */
const SAME_POINT_M = 1e-6;

/**
 * Carries the foot of a level building down into ground that falls away under it. Whatever reaches
 * down to the floor goes `depth` further in its own material, so a wall meets the ground as the
 * same wall. A plinth of plain brick on the traced footprint would show as a band of another make
 * with a ledge wherever the footprint is wider than the walls, so no building stands on one.
 *
 * A face that stands on the floor is passed on as it is, and a strip of the same face hangs under
 * each of its edges on the floor. Its own points are not moved: a wall with openings arrives here
 * cut into triangles, and a triangle from the wall's foot past the corner of a window turns its
 * back when the foot alone is pulled down. A level face at the floor, the bottom of a solid, goes
 * down whole, and a prism, a box or a cylinder standing on the floor is lengthened.
 *
 * `floorY` is the floor's world height, as the frames given here carry it: a builder that lifts
 * the building (elevatedBuilder.ts) goes around this one, not inside it.
 */
export function footedBuilder(builder: MeshBuilder, floorY: number, depth: number): MeshBuilder {
  if (depth <= 0) return builder;
  const atFoot = (frame: Frame, p: Vec3): boolean =>
    Math.abs(transformPoint(p, frame)[1] - floorY) <= AT_FLOOR_M;
  const sunk = (p: Vec3): Vec3 => [p[0], p[1] - depth, p[2]];
  const apart = (a: Vec3, b: Vec3): boolean =>
    Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) > SAME_POINT_M;
  /* The face's inside lies above an edge on the floor, so a strip wound from the edge's start
     under it to its end faces the way the face does. */
  const strips = (
    points: readonly Vec3[],
    foot: readonly boolean[],
    emit: (strip: [Vec3, Vec3, Vec3, Vec3]) => void
  ): void => {
    points.forEach((a, i) => {
      const j = (i + 1) % points.length;
      const b = points[j]!;
      if (foot[i] && foot[j] && apart(a, b)) emit([sunk(a), sunk(b), b, a]);
    });
  };
  return {
    quad: (style, frame, corners, uv) => {
      const foot = corners.map((p) => atFoot(frame, p));
      if (foot.every(Boolean)) {
        builder.quad(style, frame, corners.map(sunk) as unknown as typeof corners, uv);
        return;
      }
      builder.quad(style, frame, corners, uv);
      // A strip takes the coordinates the builder gives a face without its own.
      strips(corners, foot, (strip) => {
        builder.quad(style, frame, strip);
      });
    },
    polygon: (style, frame, outline, uvOf) => {
      const foot = outline.map((p) => atFoot(frame, p));
      if (foot.every(Boolean)) {
        // A face lays its pattern out from its points: it keeps the one it had at the floor.
        const own = new Map(outline.map((p) => [sunk(p), p] as const));
        const moved = [...own.keys()];
        builder.polygon(style, frame, moved, uvOf && ((p) => uvOf(own.get(p) ?? p)));
        return;
      }
      builder.polygon(style, frame, outline, uvOf);
      // The wall's courses run on down its foot: the strip is laid out by the wall's own rule.
      strips(outline, foot, (strip) => {
        builder.polygon(style, frame, strip, uvOf);
      });
    },
    box: (style, frame, min, max, faces) => {
      builder.box(style, frame, atFoot(frame, min) ? sunk(min) : min, max, faces);
    },
    prism: (style, frame, base, y0, y1, faces) => {
      builder.prism(style, frame, base, atFoot(frame, [0, y0, 0]) ? y0 - depth : y0, y1, faces);
    },
    cylinder: (style, frame, base, r0, r1, h, segments, faces) => {
      if (atFoot(frame, base))
        builder.cylinder(style, frame, sunk(base), r0, r1, h + depth, segments, faces);
      else builder.cylinder(style, frame, base, r0, r1, h, segments, faces);
    },
    groundPolygon: (style, polygon, y, config, uv) => {
      builder.groundPolygon(style, polygon, y, config, uv);
    },
    get vertexCount() {
      return builder.vertexCount;
    },
    get triangleCount() {
      return builder.triangleCount;
    },
    finish: () => builder.finish(),
    build: () => builder.build(),
  };
}
